import dayjs from 'dayjs';
import { useCallback } from 'react';

import { requiresPermissionCheck } from '@/feature/feature';
import { isPaymentDateEditEnabled } from '@/feature/payment-date.feature.ts';
import { useAssetActionModalStore } from '@/store/asset-action-modal.store.ts';
import {
    selectAssetStatus,
    selectBuCode,
    selectOrderType,
} from '@repo/api-adapters/selectors/customer-assets.selectors';
import { selectPaymentDetails } from '@repo/api-adapters/selectors/payment.selectors';
import { API, useCoworkerPermissions, useStoreDetails } from '@repo/hooks';
import { isOrder } from '@repo/types/order';
import { getManualPaymentsFragment, resolveTimezone } from '@repo/utils';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { logError } from '@/lib/sentry.lib.ts';

import { useAssetActionButton } from '@/components/asset-details/asset-action-buttons/asset-action-buttons.hooks';
import { MODAL } from '@/components/modals/asset-modal.types.ts';

import { getServiceBaseUrl, getServiceEndpoint } from '@/api/helpers.api.ts';
import { useAsset, useAssetParams } from '@/hooks/asset.hooks';
import { getDayBeforeOrderEarliestDeliveryDate } from '@/utils/asset-payment-date.utils.ts';
import { hasReservation } from '@/utils/asset.utils';
import { hasManualPaymentsFragment } from '@/utils/manual-payments.utils.ts';
import { getAppConfig } from '@/utils/window.utils.ts';

export const useAssetPaymentEditable = () => {
    const { assetId, assetType } = useAssetParams();
    const { data } = useAsset(assetType, assetId);
    const { isButtonDisabled } = useAssetActionButton();

    const assetBuCode = selectBuCode(data) ?? '';
    const config = getAppConfig();
    const storeDetailsBaseUrl = getServiceEndpoint(config.api.services.classicSalja, 'storeDetails');
    const { data: storeDetails } = useStoreDetails(storeDetailsBaseUrl, assetBuCode);

    const storeTimezone = storeDetails?.TimeZone ?? 'UTC';
    const resolvedTimezone = resolveTimezone(storeTimezone);
    const { isPaymentDateEditable } = selectPaymentDetails(data);

    const hasReservations = data && isOrder(data) && hasReservation(data);
    const orderType = selectOrderType(data);
    const assetStatus = selectAssetStatus(data);
    const coworkerBaseUrl = getServiceBaseUrl(config.api.services.orderTaking, 'coworker');
    const paymentDateEditContext = { assetType, orderType, assetStatus };
    const { data: permissionsData } = useCoworkerPermissions(
        coworkerBaseUrl,
        assetBuCode,
        requiresPermissionCheck(config.feature.paymentDateModification?.edit, paymentDateEditContext)
    );

    const orderPaymentBeginDate = dayjs().tz(resolvedTimezone).startOf('day').toDate();
    const orderEarliestDeliveryDate =
        data?.assetType === 'order'
            ? getDayBeforeOrderEarliestDeliveryDate(data, storeTimezone)
            : orderPaymentBeginDate;

    const daysBetween = dayjs
        .tz(orderEarliestDeliveryDate, resolvedTimezone)
        .startOf('day')
        .diff(dayjs.tz(orderPaymentBeginDate, resolvedTimezone), 'day');

    const isEditable =
        !isButtonDisabled &&
        !(hasReservations ?? false) &&
        isPaymentDateEditable &&
        daysBetween >= 0 &&
        isPaymentDateEditEnabled({
            ...paymentDateEditContext,
            permissions: permissionsData?.authorizations,
        });

    return {
        isEditable,
        daysBetween,
    };
};

export const useManualPayments = () => {
    const manualPaymentsFragment = getManualPaymentsFragment();
    const isManualPaymentsAvailable = hasManualPaymentsFragment(manualPaymentsFragment);

    const { data: canRegisterPayment } = useQuery({
        queryKey: ['canRegisterPayment', manualPaymentsFragment],
        queryFn: () => manualPaymentsFragment?.getCanRegisterPayment?.() ?? false,
        enabled: isManualPaymentsAvailable,
    });

    return {
        canRegisterPayment: canRegisterPayment ?? false,
        isManualPaymentsAvailable,
        manualPaymentsFragment: isManualPaymentsAvailable ? manualPaymentsFragment : undefined,
    };
};

export const useManualPaymentsEventHandlers = () => {
    const { assetId, assetType } = useAssetParams();
    const queryClient = useQueryClient();
    const setActiveModal = useAssetActionModalStore((state) => state.setActiveModal);

    const handleNavigateBack = useCallback((): void => {
        try {
            setActiveModal(MODAL.ORDER_PAYMENT, { logOpened: false });
        } catch (error) {
            logError(error);
        }
    }, [setActiveModal]);

    const handlePaymentChanged = useCallback((): void => {
        try {
            void queryClient
                .invalidateQueries(
                    {
                        exact: true,
                        queryKey: [API.assetDetails, assetType, assetId],
                    },
                    { throwOnError: true }
                )
                .catch(logError);
        } catch (error) {
            logError(error);
        }
    }, [assetId, assetType, queryClient]);

    return { handleNavigateBack, handlePaymentChanged };
};
